import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  PaperPlaneTilt, Trophy, Lightning, ThumbsUp, ChartLine,
  ArrowClockwise, FunnelSimple,
} from "@phosphor-icons/react";
import { api } from "../lib/api";

const KIND_META = {
  daily_wins:   { label: "Daily wins",     icon: Trophy,        accent: "text-pos" },
  strongest:    { label: "Strongest",      icon: Lightning,     accent: "text-mod" },
  power:        { label: "Power breakouts",icon: ThumbsUp,      accent: "text-pos" },
  weekly_recap: { label: "Weekly recap",   icon: ChartLine,     accent: "text-secondary-fg" },
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch {
    return iso;
  }
};

function BroadcastRow({ row }) {
  const meta = KIND_META[row.kind] || { label: row.kind, icon: PaperPlaneTilt, accent: "text-muted-fg" };
  const Icon = meta.icon;
  const syms = row.top_symbols || row.top_3_symbols || [];
  return (
    <tr
      className="border-b border-subtle/60 hover:bg-app/40 transition"
      data-testid={`tg-row-${row.kind}-${row.posted_at}`}
    >
      <td className="px-4 py-3 align-top">
        <div className="flex items-center gap-2">
          <Icon size={13} weight="bold" className={meta.accent} />
          <span className="font-mono text-[11px] uppercase tracking-widest text-primary-fg">
            {meta.label}
          </span>
        </div>
        {row.week_key && (
          <div className="text-[10px] text-muted-fg font-mono mt-0.5">
            {row.week_key}
          </div>
        )}
      </td>
      <td className="px-4 py-3 align-top text-[11px] font-mono text-secondary-fg tabular-nums">
        {fmtTime(row.posted_at)}
      </td>
      <td className="px-4 py-3 align-top text-[11px] font-mono text-secondary-fg">
        {row.items_count != null && (
          <div>
            <span className="text-primary-fg tabular-nums">{row.items_count}</span> items
          </div>
        )}
        {row.wins != null && (
          <div>
            <span className="text-pos tabular-nums">{row.wins}</span> wins ·{" "}
            <span className="text-neg tabular-nums">{row.losses ?? 0}</span> losses
            {row.win_rate != null && (
              <span className="text-muted-fg">
                {" "}· {row.win_rate}% WR
              </span>
            )}
          </div>
        )}
        {row.total_pnl_pct != null && (
          <div className={row.total_pnl_pct >= 0 ? "text-pos" : "text-neg"}>
            {row.total_pnl_pct >= 0 ? "+" : ""}
            {row.total_pnl_pct}% total
          </div>
        )}
        {row.top_conviction != null && (
          <div className="text-mod">
            ★ top conviction {row.top_conviction}
          </div>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <div className="flex flex-wrap gap-1 max-w-md">
          {syms.slice(0, 6).map((s) => (
            <span
              key={s}
              className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border border-subtle text-secondary-fg rounded-sm"
            >
              {(s || "").replace(".NS", "")}
            </span>
          ))}
          {syms.length > 6 && (
            <span className="text-[10px] text-muted-fg font-mono">+{syms.length - 6}</span>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function AdminTelegramHistory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const url = kind
        ? `/admin/telegram/broadcasts?kind=${encodeURIComponent(kind)}&limit=100`
        : `/admin/telegram/broadcasts?limit=100`;
      const { data } = await api.get(url);
      setData(data);
    } catch (e) {
      console.error("/admin/telegram/broadcasts", e);
      setErr(e?.response?.data?.detail || "Failed to load broadcast history");
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const kinds = useMemo(() => {
    const all = data?.by_kind || {};
    return [
      { k: "",             label: "All",              count: data?.total ?? 0 },
      { k: "daily_wins",   label: "Daily wins",       count: all.daily_wins ?? 0 },
      { k: "strongest",    label: "Strongest",        count: all.strongest ?? 0 },
      { k: "power",        label: "Power breakouts",  count: all.power ?? 0 },
      { k: "weekly_recap", label: "Weekly recap",     count: all.weekly_recap ?? 0 },
    ];
  }, [data]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="border border-subtle rounded-sm bg-surface/40 mx-6 my-6"
      data-testid="admin-tg-history"
    >
      <div className="px-5 py-4 border-b border-subtle flex items-center gap-3 flex-wrap">
        <PaperPlaneTilt size={14} weight="bold" className="text-pos" />
        <div className="font-mono text-xs uppercase tracking-[0.22em] text-primary-fg">
          Telegram broadcast history
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
          {data?.total ?? 0} total · channel @breakoutscanner_NSE
        </span>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="tg-history-reload"
          className="ml-auto px-3 py-1.5 border border-subtle rounded-sm text-secondary-fg hover:bg-surface-hover text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50"
        >
          <ArrowClockwise size={12} className={loading ? "animate-spin" : ""} weight="bold" />
          Reload
        </button>
      </div>

      <div className="px-5 py-3 border-b border-subtle flex items-center gap-2 flex-wrap bg-app/20">
        <FunnelSimple size={11} weight="bold" className="text-muted-fg" />
        {kinds.map((t) => {
          const active = kind === t.k;
          return (
            <button
              key={t.k || "all"}
              onClick={() => setKind(t.k)}
              data-testid={`tg-filter-${t.k || "all"}`}
              className={
                "px-2.5 py-1 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5 " +
                (active
                  ? "bg-pos-soft border-pos text-pos"
                  : "text-secondary-fg hover:bg-surface-hover")
              }
            >
              {t.label}
              <span className="text-muted-fg tabular-nums">· {t.count}</span>
            </button>
          );
        })}
      </div>

      {err && (
        <div className="m-5 p-3 border border-neg-soft bg-neg-soft text-neg text-sm rounded-sm">
          {err}
        </div>
      )}

      {loading ? (
        <div className="p-10 text-center text-muted-fg text-sm font-mono" data-testid="tg-history-loading">
          Loading…
        </div>
      ) : !(data?.items || []).length ? (
        <div className="p-10 text-center" data-testid="tg-history-empty">
          <PaperPlaneTilt size={24} className="mx-auto text-muted-fg mb-2" />
          <div className="text-primary-fg font-medium">No broadcasts yet</div>
          <div className="text-xs text-muted-fg mt-1">
            Daily wins, strongest picks, power breakouts and weekly recaps will appear here as they post.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tg-history-table">
            <thead className="text-[9px] font-mono uppercase tracking-widest text-muted-fg border-b border-subtle bg-app/40">
              <tr>
                <th className="text-left px-4 py-3">Event</th>
                <th className="text-left px-4 py-3">Posted (IST)</th>
                <th className="text-left px-4 py-3">Payload</th>
                <th className="text-left px-4 py-3">Top symbols</th>
              </tr>
            </thead>
            <tbody>
              {(data.items || []).map((r) => (
                <BroadcastRow key={`${r.kind}-${r.posted_at}`} row={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}
